import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Save,
  Phone,
  Heart,
  Pill,
  Utensils,
  Droplet,
  ShieldCheck,
  Printer,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const KEY = "silentscan_medical_id_v1";

const DEFAULT = {
  name: "",
  dob: "",
  blood_group: "",
  allergies: "",
  medications: "",
  conditions: "",
  emergency_contacts: [
    { name: "", phone: "", relation: "" },
    { name: "", phone: "", relation: "" },
  ],
  organ_donor: false,
  notes: "",
};

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : DEFAULT;
  } catch {
    return DEFAULT;
  }
};

const save = (v) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
};

export default function MedicalID() {
  const [data, setData] = useState(() => load());
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const age = useMemo(() => {
    if (!data.dob) return "";
    const d = new Date(data.dob);
    if (isNaN(d)) return "";
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  }, [data.dob]);

  const patch = (k, v) => {
    const next = { ...data, [k]: v };
    setData(next);
    save(next);
  };

  const setContact = (i, k, v) => {
    const contacts = data.emergency_contacts.map((c, idx) =>
      idx === i ? { ...c, [k]: v } : c
    );
    patch("emergency_contacts", contacts);
  };

  const printCard = () => window.print();

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(239,68,68,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40 print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="medid-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <button
            data-testid="medid-print"
            onClick={printCard}
            className="text-xs text-zinc-300 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 inline-flex items-center gap-2"
          >
            <Printer className="w-3.5 h-3.5" />
            Print
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-12">
        <div className="max-w-3xl print:hidden">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-red-300/80">
            Chapter 08 · Medical ID
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            Your details, when{" "}
            <span className="italic text-red-300">seconds matter.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            A wallet-style card first responders can use in an emergency.
            Stored on your device only.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6">
          {/* Form */}
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 print:hidden">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Personal
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Full name" testId="medid-name">
                <input
                  value={data.name}
                  onChange={(e) => patch("name", e.target.value)}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                />
              </Field>
              <Field label="Date of birth" testId="medid-dob">
                <input
                  type="date"
                  value={data.dob}
                  onChange={(e) => patch("dob", e.target.value)}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                />
              </Field>
              <Field label="Blood group" testId="medid-blood">
                <select
                  value={data.blood_group}
                  onChange={(e) => patch("blood_group", e.target.value)}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="">Select</option>
                  {BLOOD_GROUPS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Organ donor" testId="medid-organ">
                <select
                  value={data.organ_donor ? "yes" : "no"}
                  onChange={(e) => patch("organ_donor", e.target.value === "yes")}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </Field>
            </div>

            <div className="mt-5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Health facts
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <Field label="Allergies (comma-separated)" testId="medid-allergies">
                <input
                  value={data.allergies}
                  onChange={(e) => patch("allergies", e.target.value)}
                  placeholder="peanuts, penicillin…"
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                />
              </Field>
              <Field label="Current medications" testId="medid-medications">
                <input
                  value={data.medications}
                  onChange={(e) => patch("medications", e.target.value)}
                  placeholder="metformin 500 mg, telmisartan 40 mg…"
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                />
              </Field>
              <Field label="Chronic conditions" testId="medid-conditions">
                <input
                  value={data.conditions}
                  onChange={(e) => patch("conditions", e.target.value)}
                  placeholder="diabetes, asthma…"
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                />
              </Field>
              <Field label="Notes" testId="medid-notes">
                <textarea
                  value={data.notes}
                  onChange={(e) => patch("notes", e.target.value)}
                  rows={2}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400 resize-none"
                />
              </Field>
            </div>

            <div className="mt-5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Emergency contacts
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              {data.emergency_contacts.map((c, i) => (
                <div
                  key={i}
                  data-testid={`medid-contact-${i}`}
                  className="grid grid-cols-1 md:grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-black/40 p-3"
                >
                  <input
                    value={c.name}
                    onChange={(e) => setContact(i, "name", e.target.value)}
                    placeholder="Name"
                    className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                  />
                  <input
                    value={c.phone}
                    onChange={(e) => setContact(i, "phone", e.target.value)}
                    placeholder="Phone"
                    className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                  />
                  <input
                    value={c.relation}
                    onChange={(e) => setContact(i, "relation", e.target.value)}
                    placeholder="Relation"
                    className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-center gap-2 text-[11px] text-zinc-500">
              <ShieldCheck className="w-3.5 h-3.5" />
              Autosaved on your device. Nothing is sent to our servers.
            </div>
          </div>

          {/* Card preview */}
          <MedicalCard data={data} age={age} onPrint={printCard} />
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, testId, children }) => (
  <label data-testid={testId} className="block">
    <span className="block text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
      {label}
    </span>
    {children}
  </label>
);

const MedicalCard = ({ data, age, onPrint }) => (
  <motion.div
    data-testid="medid-card"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="lg:sticky lg:top-24 self-start"
  >
    <div className="rounded-3xl overflow-hidden border border-red-500/40 bg-gradient-to-br from-red-500/20 via-black to-black shadow-2xl">
      <div className="bg-red-500 text-black px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold">
          <Heart className="w-4 h-4 fill-current" />
          MEDICAL ID
        </div>
        <div className="text-[10px] font-bold tracking-widest">
          SILENTSCAN.AI
        </div>
      </div>
      <div className="p-6">
        <div className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">
          Name
        </div>
        <div className="font-display text-2xl font-semibold text-white mt-0.5">
          {data.name || "—"}
        </div>
        <div className="mt-1 flex items-center gap-4 text-xs text-zinc-300">
          {age !== "" && <div>Age {age}</div>}
          {data.blood_group && (
            <div className="inline-flex items-center gap-1">
              <Droplet className="w-3 h-3 text-red-400" />
              {data.blood_group}
            </div>
          )}
          {data.organ_donor && (
            <div className="text-emerald-300 font-medium">Organ donor</div>
          )}
        </div>

        <CardRow Icon={ShieldCheck} label="Allergies" value={data.allergies} highlight />
        <CardRow Icon={Pill} label="Medications" value={data.medications} />
        <CardRow Icon={Utensils} label="Conditions" value={data.conditions} />
        {data.notes && <CardRow Icon={Sparkles} label="Notes" value={data.notes} />}

        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-semibold">
            Emergency contacts
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            {data.emergency_contacts
              .filter((c) => c.name || c.phone)
              .map((c, i) => (
                <a
                  key={i}
                  href={c.phone ? `tel:${c.phone}` : undefined}
                  className="flex items-center justify-between text-sm text-white hover:text-red-200"
                >
                  <span className="truncate">
                    {c.name || "—"}{" "}
                    <span className="text-zinc-500 text-xs">
                      {c.relation && `· ${c.relation}`}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-red-300">
                    <Phone className="w-3 h-3" />
                    {c.phone || "—"}
                  </span>
                </a>
              ))}
            {data.emergency_contacts.every((c) => !c.name && !c.phone) && (
              <div className="text-xs text-zinc-500">
                Add at least one emergency contact.
              </div>
            )}
          </div>
        </div>

        <a
          href="tel:108"
          data-testid="medid-call-108"
          className="mt-5 flex items-center justify-center gap-2 rounded-full bg-red-500 hover:bg-red-400 text-black font-semibold py-3 text-sm"
        >
          <Phone className="w-4 h-4" />
          Call Ambulance (108)
        </a>
      </div>
    </div>
    <div className="mt-3 print:hidden text-[11px] text-zinc-500 text-center">
      Tip: print this card and keep it in your wallet.
    </div>
  </motion.div>
);

const CardRow = ({ Icon, label, value, highlight }) => (
  <div className="mt-4">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    <div
      className={`mt-1 text-sm leading-snug ${
        highlight ? "text-red-200 font-medium" : "text-zinc-100"
      }`}
    >
      {value || "—"}
    </div>
  </div>
);
